import type { ReactNode } from 'react';

import {
  Avatar,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import styles from './EmployeeDetails.module.scss';
import { EMPLOYEE_ID_PARAMETER_NAME } from '../../constants';
import {
  useGetEmployeeDetails,
  useGetEmployeeProfilePicture,
} from '@/api/REST/employees';
import { useGetEmployeeById } from '@/api/typesense/employees';
import { EntityHoverCard } from '@/components/entity/EntityHoverCard';
import { EntityField } from '@/components/ui/EntityField';
import { Icon } from '@/components/ui/Icon';
import { IconedLabel } from '@/components/ui/IconedLabel';
import { TableDrawerEmptyState } from '@/components/ui/TableDrawerEmptyState';
import { areaIcons } from '@/constants/areaIcons';
import { legacyRoutes } from '@/constants/legacyRoutes';
import { uiIcons } from '@/constants/uiIcons';
import { useHasPermissionToAccessPage } from '@/hooks/useHasPermissionToAccessPage';
import { employeeHoverCardFields } from '@/pages/projects/portfolio/helpers';
import { useLocaleStore } from '@/stores/locale';
import { formatDate, formatEmploymentDuration } from '@/utils/date';

/**
 * Display order for the working-time-day pills (Mon → Sun, matching
 * the European convention). The numeric values follow .NET's
 * DayOfWeek enum (Sun=0, Mon=1, …, Sat=6), as sent by Phoenix.
 */
const WORKING_DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

/** Reference Sunday in UTC; adding `dayOfWeek` days yields that weekday. */
const SAMPLE_SUNDAY = new Date(Date.UTC(2024, 0, 7));

const getShortDayName = (dayOfWeek: number, lang: string) => {
  const date = new Date(SAMPLE_SUNDAY);
  date.setUTCDate(SAMPLE_SUNDAY.getUTCDate() + dayOfWeek);
  return new Intl.DateTimeFormat(lang, {
    weekday: 'short',
    timeZone: 'UTC',
  }).format(date);
};

/**
 * Icon + visible label + value row. Used for the labelled fields inside
 * Organisation and Employment sections where the icon alone is not enough
 * to identify the field. EntityField is reserved for hover-card style rows
 * (icon + value, label visually hidden) and for typed renderers like
 * competencies / email / phone.
 */
const FieldRow = ({
  iconName,
  label,
  value,
}: {
  iconName: string;
  label: string;
  value?: ReactNode;
}) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
      <Icon name={iconName} color="secondary" size="small" />
      <Typography variant="label" color="textSecondary">
        {label}
      </Typography>
      {typeof value === 'string' || typeof value === 'number' ? (
        <Typography variant="label" color="secondary">
          {value}
        </Typography>
      ) : (
        value
      )}
    </Stack>
  );
};

export const EmployeeDetails = () => {
  const { t } = useTranslation('employees');
  const { lang } = useLocaleStore();
  const [searchParams] = useSearchParams();
  const selectedEmployeeId = searchParams.get(EMPLOYEE_ID_PARAMETER_NAME);

  const { data: employee, isLoading } = useGetEmployeeById(selectedEmployeeId);
  const { data: details } = useGetEmployeeDetails(selectedEmployeeId);
  const { data: profilePictureUrl } =
    useGetEmployeeProfilePicture(selectedEmployeeId);

  const { hasPermission: canEditEmployee } =
    useHasPermissionToAccessPage('EditEmployee');

  if (!selectedEmployeeId) {
    return <TableDrawerEmptyState text={t('emptyState.SelectEmployee')} />;
  }

  if (isLoading) {
    return (
      <Stack direction="row" justifyContent="center">
        <CircularProgress />
      </Stack>
    );
  }

  if (!employee) {
    return <TableDrawerEmptyState text={t('emptyState.SelectEmployee')} />;
  }

  const {
    Name,
    Initials,
    Position,
    Email,
    PrivatePhone,
    Mobile,
    HomeCity,
    Comment,
    Competencies,
    Employed,
    EmployedSince,
    LegalEntityName,
    DepartmentName,
    RoleName,
    ManagerName,
    ManagerId,
    HolidayCalendarName,
  } = employee;

  const employmentDuration = EmployedSince
    ? formatEmploymentDuration(EmployedSince, lang)
    : undefined;

  return (
    <Stack gap={4}>
      {/* First section: profile + contact + status + edit */}
      <Stack gap={3}>
        {/* Header */}
        <Stack direction="row" gap={1.5} alignItems="center">
          {profilePictureUrl ? (
            <Avatar size="medium" src={profilePictureUrl} />
          ) : (
            <Icon name={areaIcons.employees} size={40} color="secondary" />
          )}
          <Stack flex={1}>
            <Stack
              direction="row"
              gap={0.5}
              alignItems="baseline"
              flexWrap="wrap"
            >
              <Typography component="h2" variant="h4">
                {Name || t('emptyState.UnnamedEmployee')}
              </Typography>
              {Initials && (
                <Typography variant="label" color="textSecondary">
                  ({Initials})
                </Typography>
              )}
            </Stack>
            {Position && (
              <Typography component="p" variant="label" color="textSecondary">
                {Position}
              </Typography>
            )}
          </Stack>
        </Stack>

        {/* Contact — flow onto the same line when the drawer is wide enough,
            wrap onto the next line otherwise. sx is used here for the
            asymmetric column/row gap (same pattern + bug as the
            ProjectDetails sidepanel; see MUI issue #47844). */}
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          sx={{ columnGap: 2, rowGap: 1 }}
        >
          <EntityField
            field={{
              label: t('details.EmailLabel'),
              type: 'email',
              value: Email,
              hideIfEmpty: true,
            }}
          />
          <EntityField
            field={{
              label: t('details.PhoneLabel'),
              type: 'phone',
              value: PrivatePhone,
              hideIfEmpty: true,
            }}
          />
          {HomeCity && (
            <IconedLabel
              iconName={uiIcons.metadata.location}
              iconColor="secondary"
              typographyProps={{ color: 'secondary' }}
            >
              {HomeCity}
            </IconedLabel>
          )}
          <EntityField
            field={{
              label: t('details.MobileLabel'),
              type: 'phone',
              value: Mobile,
              hideIfEmpty: true,
            }}
          />
        </Stack>

        {/* Status strip */}
        <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
          <IconedLabel
            iconName={
              Employed ? uiIcons.status.success : uiIcons.state.disabled
            }
            iconColor="secondary"
          >
            {Employed ? t('status.Active') : t('status.Inactive')}
          </IconedLabel>
          {details?.awayUntil && (
            <IconedLabel iconName={uiIcons.state.away} iconColor="secondary">
              {t('details.AwayUntilLabel')}{' '}
              {formatDate(details.awayUntil, lang)}
            </IconedLabel>
          )}
        </Stack>

        {Comment && (
          <IconedLabel
            iconName={uiIcons.content.quote}
            iconColor="secondary"
            typographyProps={{ className: styles.CommentText }}
          >
            {Comment}
          </IconedLabel>
        )}

        {/* Edit details — gated by the EditEmployee permittedPage,
            mirroring how the legacy edit page itself is access-checked. */}
        {canEditEmployee && (
          <Stack direction="row" justifyContent="flex-end">
            <Button
              component={Link}
              to={legacyRoutes.organization.employee.edit(selectedEmployeeId!)}
              variant="outlined"
              color="secondary"
              size="small"
              data-automation-id="portfolio-edit-employee-button"
            >
              {t('details.EditDetailsButton')}
            </Button>
          </Stack>
        )}
      </Stack>

      <Divider />

      {/* Organisation */}
      <Stack gap={3}>
        <Stack direction="row" alignItems="center">
          <Icon
            name={uiIcons.organization.hierarchy}
            color="secondary"
            size="small"
          />
          <Typography
            component="h3"
            variant="bodyRegularMono"
            color="secondary"
          >
            {t('details.OrganisationHeading')}
          </Typography>
        </Stack>

        <FieldRow
          iconName={uiIcons.organization.legalEntity}
          label={t('details.LegalEntityLabel')}
          value={LegalEntityName}
        />
        <FieldRow
          iconName={uiIcons.organization.department}
          label={t('details.DepartmentLabel')}
          value={DepartmentName}
        />
        <FieldRow
          iconName="badge"
          label={t('details.ManagerLabel')}
          value={
            ManagerName && ManagerId ? (
              <EntityHoverCard
                entityId={ManagerId}
                config={employeeHoverCardFields}
                apiKeyName="managers"
              >
                <Typography variant="label" color="secondary">
                  {ManagerName}
                </Typography>
              </EntityHoverCard>
            ) : (
              ManagerName
            )
          }
        />
      </Stack>

      <Divider />

      {/* Employment */}
      <Stack gap={3}>
        <Stack direction="row" alignItems="center">
          <Icon
            name={uiIcons.organization.employment}
            color="secondary"
            size="small"
          />
          <Typography
            component="h3"
            variant="bodyRegularMono"
            color="secondary"
          >
            {t('details.EmploymentHeading')}
          </Typography>
        </Stack>

        <FieldRow
          iconName="label"
          label={t('details.EmployeeTypeLabel')}
          value={
            RoleName ? (
              <Chip
                className={styles.FieldChipPurple}
                label={RoleName}
                size="small"
                monoText
              />
            ) : undefined
          }
        />
        <FieldRow
          iconName="timelapse"
          label={t('details.WorkingHoursLabel')}
          value={
            <Stack direction="row" gap={0.5} flexWrap="wrap">
              {WORKING_DAY_ORDER.map((day) => {
                // While /details is in flight we render the seven pills
                // with a shimmer overlay so the row doesn't pop into
                // existence on response. The base style is identical to
                // the "off" state (fog-10), so the swap to the final
                // colors is a smooth transition rather than a flash.
                const isLoading = !details?.workingTimeByDay;
                const hours =
                  details?.workingTimeByDay?.find(
                    (entry) => entry.dayOfWeek === day,
                  )?.hours ?? 0;
                const isActive = !isLoading && hours > 0;
                const isOff = !isLoading && hours === 0;
                return (
                  <Chip
                    key={day}
                    className={cx(styles.DayChipBase, {
                      [styles.DayChipLoading]: isLoading,
                      [styles.DayChipActive]: isActive,
                      [styles.DayChipOff]: isOff,
                    })}
                    label={getShortDayName(day, lang)}
                    size="small"
                    monoText
                  />
                );
              })}
            </Stack>
          }
        />
        <FieldRow
          iconName="calendar_month"
          label={t('details.HolidayCalendarLabel')}
          value={
            HolidayCalendarName ? (
              <Chip
                className={styles.FieldChipPurple}
                label={HolidayCalendarName}
                size="small"
                monoText
              />
            ) : undefined
          }
        />
        <FieldRow
          iconName="work_history"
          label={t('details.ActiveEmployeeLabel')}
          value={employmentDuration}
        />
      </Stack>

      <Divider />

      {/* Skills */}
      <Stack gap={3}>
        <Stack direction="row" alignItems="center">
          <Icon
            name={uiIcons.organization.skills}
            color="secondary"
            size="small"
          />
          <Typography
            component="h3"
            variant="bodyRegularMono"
            color="secondary"
          >
            {t('details.CompetenciesHeading')}
          </Typography>
        </Stack>

        {Competencies && Competencies.length > 0 ? (
          <EntityField
            field={{
              field: 'Competencies',
              label: t('details.CompetenciesHeading'),
              type: 'competencies',
              value: Competencies,
            }}
          />
        ) : (
          <Typography variant="label" color="textDisabled">
            {t('emptyState.NoCompetencies')}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};
